import React, { useEffect, useState } from "react";
import { FaBan, FaCheckCircle, FaClock, FaExclamationTriangle, FaPen, FaPaperPlane } from "react-icons/fa";
import { generateInvoicePdf, generateInvoicePdfBlob } from "../../utils/invoicePdf";
import { formatPhoneDisplay } from "../../utils/phone";
import billingApi, { billingError } from "./billingApi";

const STATUS = {
  Draft: { tone: "grey", icon: FaPen, label: "Draft" },
  Sent: { tone: "blue", icon: FaPaperPlane, label: "Unpaid" },
  "Partially Paid": { tone: "amber", icon: FaClock, label: "Partially Paid" },
  Paid: { tone: "green", icon: FaCheckCircle, label: "Paid" },
  Overdue: { tone: "red", icon: FaExclamationTriangle, label: "Overdue" },
  Cancelled: { tone: "grey", icon: FaBan, label: "Cancelled" },
};

/** Invoice status with words + icon (Overdue is computed from the due date by the API). */
export function InvoiceStatus({ status, small = true }) {
  const info = STATUS[status] || STATUS.Draft;
  const Icon = info.icon;
  return (
    <span className={`wk-pill wk-pill--${info.tone}${small ? " wk-pill--sm" : ""}`}>
      <Icon aria-hidden="true" className="wk-pill__icon" />
      {info.label}
    </span>
  );
}

export function money(value, currency) {
  const n = Number(value || 0);
  const symbol = currency?.Symbol || "₹";
  return `${symbol}${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const dateFmt = new Intl.DateTimeFormat("en-IN", { day: "2-digit", month: "short", year: "numeric" });

export function fmtDate(value) {
  if (!value) return "—";
  const [y, m, d] = String(value).slice(0, 10).split("-").map(Number);
  if (!y) return "—";
  return dateFmt.format(new Date(y, m - 1, d));
}

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Clients, projects, services, payment modes, settings and the person's access ({ view, manage }). */
export function useBillingMeta() {
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    billingApi
      .meta()
      .then((m) => alive && setMeta(m))
      .catch((err) => alive && setError(billingError(err, "Could not load billing options")));
    return () => {
      alive = false;
    };
  }, [tick]);
  return { meta, error, reload: () => setTick((t) => t + 1) };
}

/** Data for utils/invoicePdf from an invoice returned by GET /client/billing/invoices/:id. */
export function invoicePdfData(inv) {
  const issuer = inv.Issuer || {};
  const extraNotes = [
    inv.Notes,
    issuer.GSTIN ? `Our GSTIN: ${issuer.GSTIN}` : null,
    issuer.Payment_Instructions ? `How to pay: ${issuer.Payment_Instructions}` : null,
    inv.Terms ? `Terms: ${inv.Terms}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ");
  return {
    invoice: {
      invoiceNo: inv.Invoice_No || "DRAFT",
      invoiceDate: fmtDate(inv.Invoice_Date),
      dueDate: fmtDate(inv.Due_Date),
      currency: inv.Currency?.Code,
      subTotal: inv.Sub_Total,
      taxAmount: inv.Tax_Amount,
      discountAmount: inv.Discount_Amount,
      totalAmount: inv.Total_Amount,
      paidAmount: inv.Paid_Amount,
      balanceAmount: inv.Balance_Amount,
      notes: extraNotes,
    },
    issuer: {
      displayName: issuer.Name,
      address: issuer.Address,
      phone: issuer.Phone,
      email: issuer.Email,
      website: issuer.Website,
    },
    billedTo: {
      name: inv.Bill_To?.Name,
      contact: inv.Bill_To?.Contact,
      email: inv.Bill_To?.Email,
      phone: formatPhoneDisplay("", inv.Bill_To?.Phone),
      address: inv.Bill_To?.Address,
      gstin: inv.Bill_To?.GSTIN,
    },
    lineItems: (inv.items || []).map((it) => ({
      item: it.Description,
      description: it.Tax_Pct ? `${issuer.Tax_Label || "Tax"} ${Number(it.Tax_Pct)}%` : "",
      quantity: Number(it.Quantity),
      rate: Number(it.Unit_Price),
      amount: Number(it.Quantity) * Number(it.Unit_Price),
    })),
    payments: (inv.payments || [])
      .filter((p) => p.Status === "Completed")
      .map((p) => ({ date: fmtDate(p.Payment_Date), amount: p.Amount, mode: p.Payment_Mode, ref: p.Reference })),
    cancelled: inv.Status === "Cancelled" ? { date: fmtDate(inv.Cancelled_On), reason: inv.Cancel_Reason } : null,
  };
}

export function downloadClientInvoicePdf(inv) {
  generateInvoicePdf(invoicePdfData(inv)).save(`${(inv.Invoice_No || "draft-invoice").replace(/[^\w.-]+/g, "_")}.pdf`);
}

export function clientInvoicePdfBase64(inv) {
  const blob = generateInvoicePdfBlob(invoicePdfData(inv));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** wa.me link (Indian numbers without a country code get 91). */
export function whatsappLink(phone, text) {
  let digits = String(phone || "").replace(/\D/g, "");
  if (digits.length === 10) digits = `91${digits}`;
  const msg = encodeURIComponent(text || "");
  return digits ? `https://wa.me/${digits}?text=${msg}` : `https://wa.me/?text=${msg}`;
}

export function invoiceMessage(inv, reminder = false) {
  const company = inv.Issuer?.Name || "us";
  const amount = money(reminder ? inv.Balance_Amount : inv.Total_Amount, inv.Currency);
  const who = inv.Bill_To?.Contact || inv.Bill_To?.Name || "";
  if (reminder) {
    return (
      `Hello ${who}, a friendly reminder that invoice ${inv.Invoice_No} has ${amount} due` +
      (inv.Due_Date ? ` (due ${fmtDate(inv.Due_Date)})` : "") +
      `. ${inv.Issuer?.Payment_Instructions ? `How to pay: ${inv.Issuer.Payment_Instructions}. ` : ""}Thank you, ${company}`
    );
  }
  return (
    `Hello ${who}, invoice ${inv.Invoice_No} from ${company} for ${amount}` +
    (inv.Due_Date ? `, due ${fmtDate(inv.Due_Date)}` : "") +
    `. ${inv.Issuer?.Payment_Instructions ? `How to pay: ${inv.Issuer.Payment_Instructions}. ` : ""}Thank you.`
  );
}
